// Companies House API client

// Placeholder company profile to use when API calls fail
export const PLACEHOLDER_COMPANY_PROFILE: CompanyProfile = {
    accounts: {
        last_accounts: {
            period_end_on: '2025-03-31',
            period_start_on: '2024-04-01',
            type: 'accounts',
            made_up_to: '2025-03-31'
        }, accounting_reference_date: {day: '31', month: '3'},
        next_accounts: {
            due_on: "",
            overdue: false,
            period_end_on: "",
            period_start_on: ""
        },
        next_due: "",
        next_made_up_to: "",
        overdue: false
    },

    company_name: 'Example Company Ltd',
    company_number: '12345678',
    can_file: false,
    company_status: "",
    confirmation_statement: {
        last_made_up_to: "",
        next_due: "",
        next_made_up_to: "",
        overdue: false
    },
    date_of_creation: "",
    date_of_cessation: "",
    etag: "",
    has_charges: false,
    has_insolvency_history: false,
    jurisdiction: "",
    links: {
        persons_with_significant_control: "",
        self: "",
        charges: "",
        filing_history: "",
        officers: ""
    },
    registered_office_address: {
        address_line_1: "",
        address_line_2: "",
        country: "",
        locality: "",
        postal_code: ""
    },
    registered_office_is_in_dispute: false,
    sic_codes: [],
    type: "",
    undeliverable_registered_office_address: false,
    has_super_secure_pscs: false
};

// Company profile interface matching the Companies House API response structure
export interface CompanyProfile {
    accounts: {
        accounting_reference_date: {
            day: string;
            month: string;
        };
        last_accounts: {
            made_up_to: string;
            period_end_on: string;
            period_start_on: string;
            type: string;
        };
        next_accounts: {
            due_on: string;
            overdue: boolean;
            period_end_on: string;
            period_start_on: string;
        };
        next_due: string;
        next_made_up_to: string;
        overdue: boolean;
    };
    can_file: boolean;
    company_name: string;
    company_number: string;
    company_status: string;
    confirmation_statement: {
        last_made_up_to: string;
        next_due: string;
        next_made_up_to: string;
        overdue: boolean;
    };
    date_of_creation: string;
    date_of_cessation: string;
    etag: string;
    has_charges: boolean;
    has_insolvency_history: boolean;
    jurisdiction: string;
    links: {
        persons_with_significant_control: string;
        self: string;
        charges: string;
        filing_history: string;
        officers: string;
    };
    registered_office_address: {
        address_line_1: string;
        address_line_2: string;
        country: string;
        locality: string;
        postal_code: string;
    };
    registered_office_is_in_dispute: boolean;
    sic_codes: string[];
    type: string;
    undeliverable_registered_office_address: boolean;
    has_super_secure_pscs: boolean;
}

/**
 * Fetches company information from the Companies House API
 * @param companyNumber The 8-character company registration number
 * @returns Promise resolving to company profile data
 */
export async function getCompany(companyNumber: string): Promise<CompanyProfile> {
    try {
        // Validate company number format
        if (!companyNumber.match(/^[0-9A-Z]{8}$/)) {
            throw new Error('Invalid company number');
        }

        // Get API key from environment variables
        const apiKey = import.meta.env.VITE_CH_API_KEY;
        if (!apiKey) {
            throw new Error('Companies House API key not found in environment variables');
        }

        // Create authorization header with API key (Basic auth with empty password)
        const authHeader = `Basic ${btoa(`${apiKey}:`)}`;

        // Make request to Companies House API
        const response = await fetch(`https://api.company-information.service.gov.uk/company/${companyNumber}`, {
            headers: {
                Authorization: authHeader,
                Accept: 'application/json'
            }
        });

        // Handle error responses
        if (!response.ok) {
            if (response.status === 404) {
                throw new Error(`Company number ${companyNumber} not found`);
            }
            throw new Error(`Companies House API error: ${response.status} ${response.statusText}`);
        }

        // Parse and return the response
        const data = await response.json();

        // Extract and return the required fields
        return data;
    } catch (error) {
        console.error(`Error fetching company data: ${error instanceof Error ? error.message : String(error)}`);
        // Return placeholder values instead of throwing an error
        return PLACEHOLDER_COMPANY_PROFILE;
    }
}
